/**
 * Batería de pruebas oficiales PsyTalent.
 * Claves internas (TestKey) desde config/tests; candidato y reclutador solo ven nombre comercial.
 */

import type { IndustryId } from '@/config/industries';
import type { TestKey } from '@/config/tests';
import { getTestDisplayName, getInstrumentId } from '@/config/tests';

export interface OfficialTest {
  testId: TestKey;
  /** Solo para anexo (opcional). */
  technicalName?: string;
  industryScope?: IndustryId[];
}

const SCOPE_HOSPITALITY: IndustryId[] = ['hospitality_food'];

/** IDs de tests que aparecen en el selector de batería (para filtrar presets). */
export const OFFICIAL_TEST_IDS = [
  'perfil_personalidad',
  'autogestion_emocional',
  'estilo_trabajo_organizacion',
  'criterio_servicio_cliente',
  'PCL_10',
  'RFB_SJ_8',
  'JK_OFFICE_ADMIN',
  'conocimiento_puesto_ventas_retail',
  'ML_8',
  'APO_6',
] as const;

export const OFFICIAL_TESTS: OfficialTest[] = [
  { testId: 'perfil_personalidad', technicalName: 'Mini-IPIP (20)' },
  { testId: 'autogestion_emocional' },
  { testId: 'estilo_trabajo_organizacion' },
  { testId: 'criterio_servicio_cliente', industryScope: SCOPE_HOSPITALITY },
  { testId: 'PCL_10' },
  { testId: 'RFB_SJ_8' },
  { testId: 'JK_OFFICE_ADMIN' },
  { testId: 'conocimiento_puesto_ventas_retail' },
  { testId: 'ML_8' },
  { testId: 'APO_6' },
];

export type TestId = TestKey | string;

/** Indica si un testKey/ID está en el catálogo oficial (evita falsos negativos). */
export function isOfficialSelected(x: string): boolean {
  return (
    OFFICIAL_TEST_IDS.includes(x as (typeof OFFICIAL_TEST_IDS)[number]) ||
    OFFICIAL_TESTS.some((t) => t.testId === x)
  );
}

/** Separa tests JK-Core (conocimiento del puesto) del resto para UI de grupo plegable. */
export function splitJKCoreTests(tests: OfficialTest[]): {
  jkTests: OfficialTest[];
  otherTests: OfficialTest[];
} {
  const jkTests = tests.filter((t) => getInstrumentId(t.testId).startsWith('JK_'));
  const otherTests = tests.filter((t) => !getInstrumentId(t.testId).startsWith('JK_'));
  return { jkTests, otherTests };
}

/** Pruebas disponibles para un rubro. */
export function getTestsForIndustry(industryId: string | null | undefined): OfficialTest[] {
  if (!industryId)
    return OFFICIAL_TESTS.filter((t) => !t.industryScope || t.industryScope.length === 0);
  return OFFICIAL_TESTS.filter(
    (t) => !t.industryScope?.length || t.industryScope.includes(industryId as IndustryId)
  );
}

/** Todas las pruebas del catálogo oficial. Siempre visibles en el selector para que el usuario pueda modificar. */
export function getAllOfficialTests(): OfficialTest[] {
  return [...OFFICIAL_TESTS];
}

/** Nombre comercial (incluye legacy para datos ya guardados). */
export function getCommercialName(testId: string): string {
  const fromCatalog = getTestDisplayName(testId);
  if (fromCatalog !== testId) return fromCatalog;
  const legacy: Record<string, string> = {
    persistencia_presion: 'Persistencia y Desempeño bajo Presión',
    competencias_interaccion: 'Competencias de Interacción Profesional',
    'mock-personality': 'Perfil Integral de Personalidad Profesional',
    'ipip-neo-120': 'Perfil Integral de Personalidad Profesional',
    'emotional-intelligence': 'Autogestión Emocional en el Trabajo',
    'social-skills': 'Competencias de Interacción Profesional',
    asrs: 'Estilo de Trabajo y Organización Laboral',
    'dass-21': 'Persistencia y Desempeño bajo Presión',
    'phq-9': 'Indicadores de Bienestar Emocional',
    'gad-7': 'Equilibrio Emocional General',
    k10: 'Equilibrio Emocional General',
    'pid5-sf': 'Tendencias de Funcionamiento Personal',
    scl27: 'Indicadores de Ajuste Psicológico General',
    t01: 'Perfil Integral de Personalidad Profesional',
    t02: 'Autogestión Emocional en el Trabajo',
    t03: 'Estilo de Trabajo y Organización Laboral',
    t04: 'Confiabilidad y Ética Profesional',
    t05: 'Competencias de Interacción Profesional',
    t06: 'Persistencia y Desempeño bajo Presión',
    t07: 'Estilo de Toma de Decisiones Laborales',
    t08: 'Responsabilidad y Control en el Trabajo',
    t09: 'Criterio Operativo y Apego a Normas',
    t10: 'Criterio de Servicio y Atención al Cliente',
  };
  return legacy[testId] ?? testId;
}

export function getTechnicalName(testId: string): string {
  const found = OFFICIAL_TESTS.find((t) => t.testId === testId)?.technicalName;
  if (found) return found;
  return testId;
}

/** Mapeo legacy → TestKey (para migrar datos guardados). */
const LEGACY_TO_NEW: Record<string, TestKey> = {
  'ipip-neo-120': 'perfil_personalidad',
  'emotional-intelligence': 'autogestion_emocional',
  asrs: 'estilo_trabajo_organizacion',
  'social-skills': 'competencias_interaccion',
  'dass-21': 'persistencia_presion',
  t01: 'perfil_personalidad',
  t02: 'autogestion_emocional',
  t03: 'estilo_trabajo_organizacion',
  t04: 'PCL_10',
  t05: 'competencias_interaccion',
  t06: 'persistencia_presion',
  t07: 'RFB_SJ_8',
  t08: 'estilo_trabajo_organizacion',
  t09: 'criterio_operativo_normas',
  t10: 'criterio_servicio_cliente',
};

export function normalizeTestId(testId: string): string {
  return LEGACY_TO_NEW[testId] ?? testId;
}

/** Reemplaza derivados por instrumentos base. RA-10: responsabilidad_control/RA_10 → estilo_trabajo_organizacion. */
export function normalizeBatteryForDerived(battery: string[]): string[] {
  const seen = new Set<string>();
  return battery
    .flatMap((id) => {
      if (id === 'responsabilidad_control' || id === 'RA_10') return ['estilo_trabajo_organizacion'];
      if (id === 'persistencia_presion' || id === 'competencias_interaccion') return ['ML_8'];
      if (id === 'confiabilidad_etica') return ['PCL_10'];
      if (id === 'criterio_operativo_normas') return ['PCL_10', 'RFB_SJ_8'];
      if (id === 'estilo_decisiones') return ['RFB_SJ_8'];
      return [id];
    })
    .filter((id) => {
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
}
